/**
 * Time — day arithmetic and date formatting — in one place.
 *
 * Five reports were each carrying their own `const DAY` and their own
 * floor-and-clamp. They agreed, which is the only reason nothing was wrong —
 * and agreement maintained by luck is the state just before a discrepancy.
 */

export const DAY = 86_400_000;

/**
 * The long date on an invoice: `2026 July 28`, en-CA, spelled month.
 *
 * The PDF and the email body render the *same* invoice, and each carried its
 * own copy of this formatter. Two copies of a date format on one document is a
 * discrepancy waiting for someone to change one of them — a clinic's bookkeeper
 * comparing the attachment against the message must never see two dates.
 */
export const longDate = (ms: number): string =>
  new Date(ms).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

/**
 * Whole days elapsed from `from` to `to`, floored and clamped at zero.
 *
 * Floored because a dispatch sent 30 hours ago is one day old, not two — an
 * ageing report that rounds up makes every row look worse than it is. Clamped
 * because a future timestamp (a clock skew, a back-dated edit) must read as
 * "today", never as a negative age on a screen.
 */
export const daysBetween = (from: number, to: number): number =>
  Math.max(0, Math.floor((to - from) / DAY));

/**
 * How long a dispatch may go unconfirmed before it is a question rather than a
 * delivery in transit.
 *
 * Three things must agree on this number or the app contradicts itself in
 * front of the client: the not-received report (which bands a row "overdue"
 * and tells the UI the threshold), the nightly sweep in `convex/maintenance.ts`
 * (which raises the `unreceived-dispatch` alert), and `convex/seed.ts` (which
 * back-dates the seeded alerts so the demo opens with the report and the alert
 * list saying the same thing). They each carried their own `4`.
 *
 * **This is our guess, not the client's number.** Gloria has not been asked how
 * long a delivery normally takes to be confirmed; if it is routinely a week,
 * the alert cries wolf and the tab stops being read. It belongs in
 * `orgSettings` once she answers — at which point this constant becomes the
 * default rather than the rule.
 */
export const UNRECEIVED_AFTER_DAYS = 4;
